/// <reference types="@angular/localize" />

import { bootstrapApplication } from '@angular/platform-browser';
import { NgSelectComponent } from '@ng-select/ng-select';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';

/**
 * Global fix for ng-select v18 clear-button (×) bug.
 *
 * The native handleMousedown uses
 *     event.target.classList.contains('ng-clear-wrapper')
 * to detect clicks on the clear button. But the visible × lives in a
 * child <span class="ng-clear">, so `event.target` is the child span —
 * the classList check fails and the handler falls through to "open the
 * dropdown" instead of clearing the selection.
 *
 * Earlier attempts (CSS pointer-events tricks, capture-phase mousedown
 * re-dispatch) did NOT reliably fix this on the user's machine. The
 * definitive fix: monkey-patch NgSelectComponent.prototype.handleMousedown
 * to use `target.closest('.ng-clear-wrapper')` instead of an exact-class
 * check — that way clicks anywhere inside the wrapper, including its
 * children, route to handleClearClick. All ng-select instances created
 * after this patch use the fixed handler.
 */
(function patchNgSelectClearBug() {
  const proto: any = (NgSelectComponent as any).prototype;
  if (!proto || typeof proto.handleMousedown !== 'function') return;
  if (proto.__clearFixApplied) return; // idempotent (HMR safety)
  const original = proto.handleMousedown;
  proto.handleMousedown = function ($event: MouseEvent) {
    const target = $event.target as HTMLElement | null;
    if (target && typeof target.closest === 'function') {
      const clearWrapper = target.closest('.ng-clear-wrapper');
      if (clearWrapper) {
        if (this.disabled) return;
        $event.preventDefault();
        $event.stopPropagation();
        if (this.hasValue) {
          this.handleClearClick();
        }
        return;
      }
    }
    return original.call(this, $event);
  };
  proto.__clearFixApplied = true;
})();

/**
 * Global fix for ng-select v18.0.0 host-class bindings.
 *
 * The component declares its host classes as
 *     "class.ng-select-opened": "this.isOpen"   (and multiple / searchable /
 *     typeahead / taggable / clearable)
 * but those members are signals, so the binding reads the signal FUNCTION,
 * which is always truthy: every <ng-select> on the page carries
 * `ng-select-opened`, `ng-select-multiple`, ... permanently and the theme
 * cannot tell an open combo from a closed one.
 *
 * Angular only rewrites a class binding when its value changes, and the
 * function identity never does, so toggling the classes on the host element
 * ourselves after the relevant lifecycle points sticks. Fixed upstream in
 * later releases; drop this once @ng-select/ng-select is upgraded.
 */
(function patchNgSelectHostClasses() {
  const proto: any = (NgSelectComponent as any).prototype;
  if (!proto || proto.__hostClassFixApplied) return;
  const read = (v: any) => (typeof v === 'function' ? v() : v);
  const sync = (self: any) => {
    const el: HTMLElement | undefined = self?.element;
    if (!el?.classList) return;
    el.classList.toggle('ng-select-opened', !!read(self.isOpen));
    el.classList.toggle('ng-select-multiple', !!read(self.multiple));
    el.classList.toggle('ng-select-searchable', read(self.searchable) !== false);
    el.classList.toggle('ng-select-typeahead', !!read(self.typeahead));
    el.classList.toggle('ng-select-taggable', !!read(self.addTag));
    el.classList.toggle('ng-select-clearable', read(self.clearable) !== false);
  };
  for (const name of ['ngAfterViewInit', 'ngOnChanges', 'open', 'close', 'detectChanges']) {
    const original = proto[name];
    if (typeof original !== 'function') continue;
    proto[name] = function (...args: any[]) {
      const result = original.apply(this, args);
      sync(this);
      if (name === 'open' && read(this.isOpen)) {
        // The search field is hidden while closed (it renders inside the
        // dropdown panel), so ng-select's own focus() call ran against a
        // display:none element. Focus it now that the open class is on.
        const input: HTMLInputElement | undefined = read(this.searchInput)?.nativeElement;
        if (input) {
          if (!input.placeholder) input.placeholder = 'Type to search…';
          input.focus({ preventScroll: true });
        }
      }
      return result;
    };
  }
  proto.__hostClassFixApplied = true;
})();

bootstrapApplication(AppComponent, appConfig)
  .catch((err) => console.error(err));
